import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';

const EmployeeListLayer = () => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [perPage, setPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [departments, setDepartments] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
    });

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [editFormData, setEditFormData] = useState({
        employee_name: '',
        position: '',
        department: '',
        expire_date: '',
        status: '',
        code: '',
    });
    const [editImageFile, setEditImageFile] = useState(null);
    const [editImagePreview, setEditImagePreview] = useState(null);
    const [editError, setEditError] = useState('');
    const [editValidationErrors, setEditValidationErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);

    // Add modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [addFormData, setAddFormData] = useState({
        employee_name: '',
        position: '',
        department: '',
        expire_date: '',
        status: 'active',
    });
    const [addImageFile, setAddImageFile] = useState(null);
    const [addImagePreview, setAddImagePreview] = useState(null);
    const [addError, setAddError] = useState('');
    const [addValidationErrors, setAddValidationErrors] = useState({});
    const [isCreating, setIsCreating] = useState(false);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingEmployee, setDeletingEmployee] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // View modal state
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingEmployee, setViewingEmployee] = useState(null);

    // QR Code modal state
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrEmployee, setQrEmployee] = useState(null);
    const qrCodeRef = useRef(null);

    const fetchEmployees = async (page = 1) => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const params = new URLSearchParams({
                per_page: perPage,
                page: page,
            });

            if (statusFilter) {
                params.append('status', statusFilter);
            }

            if (departmentFilter) {
                params.append('department', departmentFilter);
            }

            if (searchQuery) {
                params.append('search', searchQuery);
            }

            const response = await fetch(`${apiUrl}/employees?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/sign-in', { replace: true });
                    return;
                }
                throw new Error(`Failed to fetch employees: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success) {
                setEmployees(data.data || []);
                setPagination(data.pagination || {
                    current_page: 1,
                    total: 0,
                    last_page: 1,
                    from: 0,
                    to: 0,
                });
            } else {
                throw new Error(data.message || 'Failed to fetch employees');
            }
        } catch (err) {
            console.error('Error fetching employees:', err);
            setError(err.message || 'An error occurred while fetching employees');
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/employees/departments`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setDepartments(data.data || []);
                }
            }
        } catch (err) {
            console.error('Error fetching departments:', err);
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchDepartments();
    }, [perPage, statusFilter, departmentFilter, searchQuery]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) {
            fetchEmployees(newPage);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
    };

    const handleDepartmentFilter = (e) => {
        setDepartmentFilter(e.target.value);
    };

    const handlePerPageChange = (e) => {
        setPerPage(Number(e.target.value));
    };

    const openEditModal = (employee) => {
        setEditingEmployee(employee);
        
        // Format expire_date to YYYY-MM-DD format for date input
        let formattedExpireDate = '';
        if (employee.expire_date) {
            const date = new Date(employee.expire_date);
            formattedExpireDate = date.toISOString().split('T')[0];
        }
        
        setEditFormData({
            employee_name: employee.employee_name,
            position: employee.position,
            department: employee.department,
            expire_date: formattedExpireDate,
            status: employee.status,
            code: employee.code,
        });
        setEditImagePreview(employee.image_url || null);
        setEditImageFile(null);
        setEditError('');
        setEditValidationErrors({});
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingEmployee(null);
        setEditFormData({
            employee_name: '',
            position: '',
            department: '',
            expire_date: '',
            status: '',
            code: '',
        });
        setEditImageFile(null);
        setEditImagePreview(null);
        setEditError('');
        setEditValidationErrors({});
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (editValidationErrors[name]) {
            setEditValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleEditImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!validTypes.includes(file.type)) {
                setEditValidationErrors(prev => ({
                    ...prev,
                    image: ['Please select a valid image file']
                }));
                return;
            }
            if (file.size > 2048 * 1024) {
                setEditValidationErrors(prev => ({
                    ...prev,
                    image: ['Image size must be less than 2MB']
                }));
                return;
            }
            setEditImageFile(file);
            setEditImagePreview(URL.createObjectURL(file));
            if (editValidationErrors.image) {
                setEditValidationErrors(prev => ({
                    ...prev,
                    image: ''
                }));
            }
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setIsUpdating(true);
        setEditError('');
        setEditValidationErrors({});

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const formDataToSend = new FormData();
            formDataToSend.append('employee_name', editFormData.employee_name);
            formDataToSend.append('position', editFormData.position);
            formDataToSend.append('department', editFormData.department);
            formDataToSend.append('expire_date', editFormData.expire_date);
            formDataToSend.append('status', editFormData.status);
            formDataToSend.append('_method', 'PUT');
            
            if (editImageFile) {
                formDataToSend.append('image', editImageFile);
            }

            const response = await fetch(`${apiUrl}/employees/${editingEmployee.id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formDataToSend,
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 422 && data.errors) {
                    setEditValidationErrors(data.errors);
                    throw new Error('Please fix the validation errors');
                }
                throw new Error(data.message || 'Failed to update employee');
            }

            if (data.success) {
                await fetchEmployees(pagination.current_page);
                closeEditModal();
            }
        } catch (err) {
            console.error('Error updating employee:', err);
            setEditError(err.message || 'An error occurred while updating employee');
        } finally {
            setIsUpdating(false);
        }
    };

    const openAddModal = () => {
        setAddFormData({
            employee_name: '',
            position: '',
            department: '',
            expire_date: '',
            status: 'active',
        });
        setAddImageFile(null);
        setAddImagePreview(null);
        setAddError('');
        setAddValidationErrors({});
        setShowAddModal(true);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setAddFormData({
            employee_name: '',
            position: '',
            department: '',
            expire_date: '',
            status: 'active',
        });
        setAddImageFile(null);
        setAddImagePreview(null);
        setAddError('');
        setAddValidationErrors({});
    };

    const handleAddInputChange = (e) => {
        const { name, value } = e.target;
        setAddFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (addValidationErrors[name]) {
            setAddValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleAddImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!validTypes.includes(file.type)) {
                setAddValidationErrors(prev => ({
                    ...prev,
                    image: ['Please select a valid image file']
                }));
                return;
            }
            if (file.size > 2048 * 1024) {
                setAddValidationErrors(prev => ({
                    ...prev,
                    image: ['Image size must be less than 2MB']
                }));
                return;
            }
            setAddImageFile(file);
            setAddImagePreview(URL.createObjectURL(file));
            if (addValidationErrors.image) {
                setAddValidationErrors(prev => ({
                    ...prev,
                    image: ''
                }));
            }
        }
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        setAddError('');
        setAddValidationErrors({});

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const formDataToSend = new FormData();
            formDataToSend.append('employee_name', addFormData.employee_name);
            formDataToSend.append('position', addFormData.position);
            formDataToSend.append('department', addFormData.department);
            formDataToSend.append('expire_date', addFormData.expire_date);
            formDataToSend.append('status', addFormData.status);
            
            if (addImageFile) {
                formDataToSend.append('image', addImageFile);
            }

            const response = await fetch(`${apiUrl}/employees`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formDataToSend,
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 422 && data.errors) {
                    setAddValidationErrors(data.errors);
                    throw new Error('Please fix the validation errors');
                }
                throw new Error(data.message || 'Failed to create employee');
            }

            if (data.success) {
                await fetchEmployees(1);
                await fetchDepartments();
                closeAddModal();
            }
        } catch (err) {
            console.error('Error creating employee:', err);
            setAddError(err.message || 'An error occurred while creating employee');
        } finally {
            setIsCreating(false);
        }
    };

    const openDeleteModal = (employee) => {
        setDeletingEmployee(employee);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletingEmployee(null);
    };

    const handleDelete = async () => {
        if (!deletingEmployee) return;

        setIsDeleting(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/employees/${deletingEmployee.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok && response.status !== 204) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to delete employee');
            }

            await fetchEmployees(pagination.current_page);
            closeDeleteModal();
        } catch (err) {
            console.error('Error deleting employee:', err);
            alert(err.message || 'An error occurred while deleting employee');
        } finally {
            setIsDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const isExpired = (expireDate) => {
        if (!expireDate) return false;
        return new Date(expireDate) < new Date();
    };

    const openViewModal = (employee) => {
        setViewingEmployee(employee);
        setShowViewModal(true);
    };

    const closeViewModal = () => {
        setShowViewModal(false);
        setViewingEmployee(null);
    };

    const openQRModal = (employee) => {
        setQrEmployee(employee);
        setShowQRModal(true);
    };

    const closeQRModal = () => {
        setShowQRModal(false);
        setQrEmployee(null);
    };

    const downloadQRCode = () => {
        if (!qrCodeRef.current) return;
        
        const canvas = qrCodeRef.current.querySelector('canvas');
        if (canvas) {
            const url = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = `${qrEmployee.employee_name}.png`;
            link.href = url;
            link.click();
        }
    };

    if (loading && employees.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className='card h-100 p-0 radius-12'>
            <div className='card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between'>
                <div className='d-flex align-items-center flex-wrap gap-3'>
                    <span className='text-md fw-medium text-secondary-light mb-0'>Show</span>
                    <select
                        className='form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px'
                        value={perPage}
                        onChange={handlePerPageChange}
                    >
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                    </select>
                    <select
                        className='form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px'
                        value={statusFilter}
                        onChange={handleStatusFilter}
                    >
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                    <select
                        className='form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px'
                        value={departmentFilter}
                        onChange={handleDepartmentFilter}
                    >
                        <option value="">All Departments</option>
                        {departments.map((dept, index) => (
                            <option key={index} value={dept}>{dept}</option>
                        ))}
                    </select>
                    <form className='navbar-search'>
                        <input
                            type='text'
                            className='bg-base h-40-px w-auto'
                            name='search'
                            placeholder='Search'
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                        <Icon icon='ion:search-outline' className='icon' />
                    </form>
                </div>
                <button
                    type='button'
                    className='btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2'
                    onClick={openAddModal}
                >
                    <Icon icon='ic:baseline-plus' className='icon text-xl line-height-1' />
                    Add New Employee
                </button>
            </div>
            <div className='card-body p-24'>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                <div className='table-responsive scroll-sm'>
                    <table className='table bordered-table sm-table mb-0'>
                        <thead>
                            <tr>
                                <th scope='col'>S.L</th>
                                <th scope='col'>Image</th>
                                <th scope='col'>Employee Code</th>
                                <th scope='col'>Name</th>
                                <th scope='col'>Position</th>
                                <th scope='col'>Department</th>
                                <th scope='col'>Expire Date</th>
                                <th scope='col'>Status</th>
                                <th scope='col' className='text-center'>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="text-center py-4">
                                        No employees found
                                    </td>
                                </tr>
                            ) : (
                                employees.map((employee, index) => {
                                    const expired = isExpired(employee.expire_date);
                                    return (
                                        <tr key={employee.id}>
                                            <td>
                                                <span className='text-md mb-0 fw-normal text-secondary-light'>
                                                    {pagination.from + index}
                                                </span>
                                            </td>
                                            <td>
                                                {employee.image_url ? (
                                                    <img 
                                                        src={employee.image_url} 
                                                        alt={employee.employee_name}
                                                        style={{ 
                                                            width: '40px', 
                                                            height: '40px', 
                                                            objectFit: 'cover', 
                                                            borderRadius: '8px',
                                                            border: '2px solid #e0e0e0'
                                                        }}
                                                    />
                                                ) : (
                                                    <div 
                                                        style={{ 
                                                            width: '40px', 
                                                            height: '40px', 
                                                            backgroundColor: '#f0f0f0',
                                                            borderRadius: '8px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '12px',
                                                            color: '#999'
                                                        }}
                                                    >
                                                        No Img
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span className='text-md mb-0 fw-semibold text-primary-600'>
                                                    {employee.code}
                                                </span>
                                            </td>
                                            <td>
                                                <span className='text-md mb-0 fw-normal text-secondary-light'>
                                                    {employee.employee_name}
                                                </span>
                                            </td>
                                            <td>
                                                <span className='text-md mb-0 fw-normal text-secondary-light'>
                                                    {employee.position}
                                                </span>
                                            </td>
                                            <td>
                                                <span className='text-md mb-0 fw-normal text-secondary-light'>
                                                    {employee.department}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`text-md mb-0 fw-normal ${expired ? 'text-danger-600' : 'text-secondary-light'}`}>
                                                    {formatDate(employee.expire_date)}
                                                    {expired && <span className='ms-2 badge bg-danger-focus text-danger-main'>Expired</span>}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge ${employee.status === 'active' ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'} px-24 py-4 radius-4 fw-medium text-sm`}>
                                                    {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                                                </span>
                                            </td>
                                            <td className='text-center'>
                                                <div className='d-flex align-items-center gap-10 justify-content-center'>
                                                    <button
                                                        type='button'
                                                        className='bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle'
                                                        onClick={() => openViewModal(employee)}
                                                        title='View Employee'
                                                    >
                                                        <Icon icon='mdi:eye' className='menu-icon' />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='bg-primary-focus bg-hover-primary-200 text-primary-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle'
                                                        onClick={() => openQRModal(employee)}
                                                        title='Generate QR Code'
                                                    >
                                                        <Icon icon='mdi:qrcode' className='menu-icon' />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle'
                                                        onClick={() => openEditModal(employee)}
                                                        title='Edit Employee'
                                                    >
                                                        <Icon icon='lucide:edit' className='menu-icon' />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle'
                                                        onClick={() => openDeleteModal(employee)}
                                                        title='Delete Employee'
                                                    >
                                                        <Icon icon='mingcute:delete-2-line' />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pagination.total > 0 && (
                    <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24'>
                        <span>Showing {pagination.from} to {pagination.to} of {pagination.total} entries</span>
                        <ul className='pagination d-flex flex-wrap align-items-center gap-2 justify-content-center'>
                            <li className='page-item'>
                                <button
                                    className='page-link bg-neutral-300 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md'
                                    onClick={() => handlePageChange(pagination.current_page - 1)}
                                    disabled={pagination.current_page === 1}
                                >
                                    <Icon icon='ep:d-arrow-left' />
                                </button>
                            </li>
                            {[...Array(pagination.last_page)].map((_, index) => (
                                <li key={index + 1} className='page-item'>
                                    <button
                                        className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px ${pagination.current_page === index + 1 ? 'bg-primary-600 text-white' : 'bg-neutral-300 text-secondary-light'}`}
                                        onClick={() => handlePageChange(index + 1)}
                                    >
                                        {index + 1}
                                    </button>
                                </li>
                            ))}
                            <li className='page-item'>
                                <button
                                    className='page-link bg-neutral-300 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md'
                                    onClick={() => handlePageChange(pagination.current_page + 1)}
                                    disabled={pagination.current_page === pagination.last_page}
                                >
                                    <Icon icon='ep:d-arrow-right' />
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Employee</h5>
                                <button type="button" className="btn-close" onClick={closeEditModal}></button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    {editError && (
                                        <div className="alert alert-danger" role="alert">
                                            {editError}
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label">Employee Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editFormData.code}
                                            disabled
                                        />
                                        <small className="text-muted">Code cannot be changed</small>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Employee Image</label>
                                        <input
                                            type="file"
                                            className={`form-control ${editValidationErrors.image ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png,image/gif"
                                            onChange={handleEditImageChange}
                                        />
                                        {editValidationErrors.image && (
                                            <div className="invalid-feedback">
                                                {editValidationErrors.image[0]}
                                            </div>
                                        )}
                                        {editImagePreview && (
                                            <div className="mt-2">
                                                <img 
                                                    src={editImagePreview} 
                                                    alt="Preview" 
                                                    style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover', borderRadius: '8px' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Magaca / Name *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${editValidationErrors.employee_name ? 'is-invalid' : ''}`}
                                            name="employee_name"
                                            value={editFormData.employee_name}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                        {editValidationErrors.employee_name && (
                                            <div className="invalid-feedback">
                                                {editValidationErrors.employee_name[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Xilka / Position *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${editValidationErrors.position ? 'is-invalid' : ''}`}
                                            name="position"
                                            value={editFormData.position}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                        {editValidationErrors.position && (
                                            <div className="invalid-feedback">
                                                {editValidationErrors.position[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Waax / Department *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${editValidationErrors.department ? 'is-invalid' : ''}`}
                                            name="department"
                                            value={editFormData.department}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                        {editValidationErrors.department && (
                                            <div className="invalid-feedback">
                                                {editValidationErrors.department[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Dhacayo / Expiry Date <span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            className={`form-control ${editValidationErrors.expire_date ? 'is-invalid' : ''}`}
                                            name="expire_date"
                                            value={editFormData.expire_date}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                        {editValidationErrors.expire_date && (
                                            <div className="invalid-feedback">
                                                {editValidationErrors.expire_date[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Status *</label>
                                        <select
                                            className={`form-select ${editValidationErrors.status ? 'is-invalid' : ''}`}
                                            name="status"
                                            value={editFormData.status}
                                            onChange={handleEditInputChange}
                                            required
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                        {editValidationErrors.status && (
                                            <div className="invalid-feedback">
                                                {editValidationErrors.status[0]}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isUpdating}>
                                        {isUpdating ? 'Updating...' : 'Update Employee'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Modal */}
            {showAddModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Employee</h5>
                                <button type="button" className="btn-close" onClick={closeAddModal}></button>
                            </div>
                            <form onSubmit={handleAddSubmit}>
                                <div className="modal-body">
                                    {addError && (
                                        <div className="alert alert-danger" role="alert">
                                            {addError}
                                        </div>
                                    )}
                                    <div className="alert alert-info" role="alert">
                                        <small>Employee code will be generated automatically</small>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Employee Image <span className="text-danger">*</span></label>
                                        <input
                                            type="file"
                                            className={`form-control ${addValidationErrors.image ? 'is-invalid' : ''}`}
                                            accept="image/jpeg,image/jpg,image/png,image/gif"
                                            onChange={handleAddImageChange}
                                            required
                                        />
                                        {addValidationErrors.image && (
                                            <div className="invalid-feedback">
                                                {addValidationErrors.image[0]}
                                            </div>
                                        )}
                                        {addImagePreview && (
                                            <div className="mt-2">
                                                <img 
                                                    src={addImagePreview} 
                                                    alt="Preview" 
                                                    style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover', borderRadius: '8px' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Magaca / Name *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${addValidationErrors.employee_name ? 'is-invalid' : ''}`}
                                            name="employee_name"
                                            value={addFormData.employee_name}
                                            onChange={handleAddInputChange}
                                            required
                                        />
                                        {addValidationErrors.employee_name && (
                                            <div className="invalid-feedback">
                                                {addValidationErrors.employee_name[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Xilka / Position *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${addValidationErrors.position ? 'is-invalid' : ''}`}
                                            name="position"
                                            value={addFormData.position}
                                            onChange={handleAddInputChange}
                                            required
                                        />
                                        {addValidationErrors.position && (
                                            <div className="invalid-feedback">
                                                {addValidationErrors.position[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Waax / Department *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${addValidationErrors.department ? 'is-invalid' : ''}`}
                                            name="department"
                                            value={addFormData.department}
                                            onChange={handleAddInputChange}
                                            required
                                        />
                                        {addValidationErrors.department && (
                                            <div className="invalid-feedback">
                                                {addValidationErrors.department[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Dhacayo / Expiry Date <span className="text-danger">*</span></label>
                                        <input
                                            type="date"
                                            className={`form-control ${addValidationErrors.expire_date ? 'is-invalid' : ''}`}
                                            name="expire_date"
                                            value={addFormData.expire_date}
                                            onChange={handleAddInputChange}
                                            min={new Date().toISOString().split('T')[0]}
                                            required
                                        />
                                        {addValidationErrors.expire_date && (
                                            <div className="invalid-feedback">
                                                {addValidationErrors.expire_date[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Status *</label>
                                        <select
                                            className={`form-select ${addValidationErrors.status ? 'is-invalid' : ''}`}
                                            name="status"
                                            value={addFormData.status}
                                            onChange={handleAddInputChange}
                                            required
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                        {addValidationErrors.status && (
                                            <div className="invalid-feedback">
                                                {addValidationErrors.status[0]}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeAddModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isCreating}>
                                        {isCreating ? 'Creating...' : 'Create Employee'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && deletingEmployee && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button type="button" className="btn-close" onClick={closeDeleteModal}></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete employee <strong>{deletingEmployee.employee_name}</strong> ({deletingEmployee.code})?</p>
                                <p className="text-danger">This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeDeleteModal}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={isDeleting}>
                                    {isDeleting ? 'Deleting...' : 'Delete Employee'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Employee Modal */}
            {showViewModal && viewingEmployee && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary-50">
                                <h5 className="modal-title">
                                    <Icon icon='mdi:account-details' className='me-2' />
                                    Employee Details
                                </h5>
                                <button type="button" className="btn-close" onClick={closeViewModal}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="row">
                                    <div className="col-md-4 text-center mb-3">
                                        {viewingEmployee.image_url ? (
                                            <img 
                                                src={viewingEmployee.image_url} 
                                                alt={viewingEmployee.employee_name}
                                                className="img-fluid rounded shadow"
                                                style={{ 
                                                    maxWidth: '100%', 
                                                    maxHeight: '300px',
                                                    objectFit: 'cover',
                                                    border: '3px solid #e0e0e0'
                                                }}
                                            />
                                        ) : (
                                            <div 
                                                className="d-flex align-items-center justify-content-center rounded shadow"
                                                style={{ 
                                                    width: '100%', 
                                                    height: '300px', 
                                                    backgroundColor: '#f0f0f0',
                                                    border: '3px solid #e0e0e0'
                                                }}
                                            >
                                                <Icon icon='mdi:account-circle' style={{ fontSize: '100px', color: '#ccc' }} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-8">
                                        <div className="mb-3">
                                            <label className="fw-bold text-secondary mb-1">Employee Code:</label>
                                            <p className="text-primary fs-5 fw-semibold mb-0">{viewingEmployee.code}</p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="fw-bold text-secondary mb-1">Employee Name:</label>
                                            <p className="mb-0">{viewingEmployee.employee_name}</p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="fw-bold text-secondary mb-1">Position:</label>
                                            <p className="mb-0">{viewingEmployee.position}</p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="fw-bold text-secondary mb-1">Department:</label>
                                            <p className="mb-0">{viewingEmployee.department}</p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="fw-bold text-secondary mb-1">Expire Date:</label>
                                            <p className={`mb-0 ${isExpired(viewingEmployee.expire_date) ? 'text-danger' : ''}`}>
                                                {formatDate(viewingEmployee.expire_date)}
                                                {isExpired(viewingEmployee.expire_date) && 
                                                    <span className='ms-2 badge bg-danger'>Expired</span>
                                                }
                                            </p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="fw-bold text-secondary mb-1">Status:</label>
                                            <p className="mb-0">
                                                <span className={`badge ${viewingEmployee.status === 'active' ? 'bg-success' : 'bg-danger'} px-3 py-2`}>
                                                    {viewingEmployee.status.charAt(0).toUpperCase() + viewingEmployee.status.slice(1)}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="mb-3">
                                            <label className="fw-bold text-secondary mb-1">Created At:</label>
                                            <p className="mb-0 text-muted">{formatDate(viewingEmployee.created_at)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeViewModal}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* QR Code Modal */}
            {showQRModal && qrEmployee && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-primary-50">
                                <h5 className="modal-title">
                                    <Icon icon='mdi:qrcode' className='me-2' />
                                    Generate QR Code
                                </h5>
                                <button type="button" className="btn-close" onClick={closeQRModal}></button>
                            </div>
                            <div className="modal-body text-center p-4">
                                <div className="mb-3">
                                    <h6 className="fw-bold">{qrEmployee.employee_name}</h6>
                                    <p className="text-muted mb-1">Code: <span className="text-primary fw-semibold">{qrEmployee.code}</span></p>
                                    <p className="text-muted small">Department: {qrEmployee.department}</p>
                                </div>
                                <div className="d-flex justify-content-center mb-3" ref={qrCodeRef}>
                                    <div className="p-3 bg-white rounded shadow">
                                        <QRCodeCanvas 
                                            value={`${window.location.origin}/profile/${qrEmployee.code}`}
                                            size={256}
                                            level="H"
                                            includeMargin={true}
                                        />
                                    </div>
                                </div>
                                <div className="alert alert-info mb-0">
                                    <small>
                                        <Icon icon='mdi:information' className='me-1' />
                                        QR Code URL: <strong>{window.location.origin}/profile/{qrEmployee.code}</strong>
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeQRModal}>
                                    Close
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={downloadQRCode}
                                >
                                    <Icon icon='mdi:download' className='me-2' />
                                    Download QR Code
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeListLayer;
